import { getChatCompletion } from './aiIntegrations/chatCompletion';

const FLIGHT_INTENT_SYSTEM_PROMPT = `You are a travel assistant that extracts structured flight search parameters from natural language.

Given a user's travel request, extract and return ONLY a valid JSON object with these fields:
- origin: string (3-letter IATA airport code, e.g. "JFK")
- destination: string (3-letter IATA airport code, e.g. "LHR")
- departureDate: string (ISO date format YYYY-MM-DD)
- returnDate: string or null (ISO date format YYYY-MM-DD, null if one-way)
- passengers: object with keys: adults (number, min 1), children (number), infants (number)
- cabinClass: string, one of: "economy", "premium_economy", "business", "first"

Rules:
- Always use IATA airport codes (3 letters). For city names, use the main airport code (e.g. New York → JFK, London → LHR, Los Angeles → LAX, Paris → CDG, Tokyo → NRT).
- If a relative date is given (e.g. "next Friday", "in 2 weeks"), calculate from today's date.
- If no return date is mentioned, set returnDate to null.
- If no passenger count is mentioned, default to adults: 1, children: 0, infants: 0.
- If no cabin class is mentioned, default to "economy".
- Return ONLY the JSON object, no explanation, no markdown, no code blocks.

Today's date: ${new Date()?.toISOString()?.split('T')?.[0]}`;

/**
 * Parse natural language travel intent into structured Duffel search parameters.
 * Uses Perplexity via AWS Lambda — the API key never touches the frontend.
 *
 * @param {string} userInput - Natural language flight request
 * @returns {Promise<{origin, destination, departureDate, returnDate, passengers, cabinClass}>}
 */
export async function parseFlightIntent(userInput) {
  if (!userInput?.trim()) {
    throw new Error('Please describe your flight to get started.');
  }

  const response = await getChatCompletion(
    'PERPLEXITY',
    'perplexity/sonar-pro',
    [
      { role: 'system', content: FLIGHT_INTENT_SYSTEM_PROMPT },
      { role: 'user', content: userInput?.trim() },
    ],
    {
      temperature: 0.1,
      max_tokens: 300,
    }
  );

  // Extract content from the response
  const content = response?.choices?.[0]?.message?.content || '';

  if (!content) {
    throw new Error('Could not understand your travel request. Please try again.');
  }

  // Strip any markdown code fences if present
  const cleaned = content?.replace(/```json\s*/gi, '')?.replace(/```\s*/gi, '')?.trim();

  let parsed;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    throw new Error('Could not parse flight details from your request. Please be more specific (e.g. "Fly from New York to London on June 15, 2 adults, economy").');
  }

  // Validate required fields
  if (!parsed?.origin || !parsed?.destination || !parsed?.departureDate) {
    throw new Error('Please include origin, destination, and departure date in your request.');
  }

  // Ensure IATA codes are uppercase
  parsed.origin = parsed?.origin?.toUpperCase();
  parsed.destination = parsed?.destination?.toUpperCase();

  // Ensure passengers object has all required keys
  parsed.passengers = {
    adults: parsed?.passengers?.adults ?? 1,
    children: parsed?.passengers?.children ?? 0,
    infants: parsed?.passengers?.infants ?? 0,
  };

  // Ensure cabinClass is valid
  const validCabins = ['economy', 'premium_economy', 'business', 'first'];
  if (!validCabins?.includes(parsed?.cabinClass)) {
    parsed.cabinClass = 'economy';
  }

  return parsed;
}
